"use client";

import React from "react";
import { addYears, isAfter, subYears } from "date-fns";
import { OfferResponse } from "@/types/simpego-api";
import { fetchBreeds } from "@/api/simpego";
import { useAgentId } from "@/context/AgentIdContext";
import { getTodayAtMidnight, createProductVariant, ProductVariant } from "@/utils/funnel";
import { isLocalStorageAvailable, loadFunnelFromStorage, saveFunnelToStorage, SerializedFunnelData } from "@/hooks/funnelPersistence";
import { hydrateValuesFromStorage, getDefaultValues } from "@/hooks/funnelSerialization";
import i18n from "@/lib/i18n";
import productData from "@/data/productData.json";

const STORAGE_KEY = "sip-pet-widget-funnelState";

export interface FunnelState {
  contractId: string | null;
  contractToken: string | null;
  offerAgentId: string | null;
  agentId: string | null;
  whitelabelPartner: string | null;
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  bundlePrices: { S: number; M: number; L: number } | null;
  selectedProduct: ProductVariant | null;
  finalPrice: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  street: string | null;
  housenumber: string | null;
  city: string | null;
  zipCode: string | null;
  country: string | null;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  isOfferLoadingFromUrl: boolean;
  isHydrated: boolean;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail: string | null;
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
}

export const useFunnelState = () => {
  const { agentId: contextAgentId, isDirectAgent, agentToken } = useAgentId();
  const previousContextAgentIdRef = React.useRef<string | null>(contextAgentId);

  const [contractId, setContractId] = React.useState<string | null>(null);
  const [contractToken, setContractToken] = React.useState<string | null>(null);
  const [offerAgentId, setOfferAgentId] = React.useState<string | null>(null);
  const [storedAgentId, setStoredAgentId] = React.useState<string | null>(null);
  const [whitelabelPartner, setWhitelabelPartner] = React.useState<string | null>(null);
  const [petType, setPetType] = React.useState<"cat" | "dog" | null>(null);
  const [petName, setPetName] = React.useState<string | null>(null);
  const [breed, setBreed] = React.useState<string | null>(null);
  const [breedLabel, setBreedLabel] = React.useState<string | null>(null);
  const [birthDate, setBirthDate] = React.useState<Date | undefined>(undefined);
  const [selectedBreedType, setSelectedBreedType] = React.useState<"purebred" | "crossbreed" | "mixed" | null>(null);
  const [gender, setGender] = React.useState<"male" | "female" | "unknown" | null>(null);
  const [isCastrated, setIsCastrated] = React.useState<boolean>(false);
  const [isHouseCat, setIsHouseCat] = React.useState<boolean>(false);
  const [selectedDogSize, setSelectedDogSize] = React.useState<"small" | "large" | null>(null);
  const [chipNumber, setChipNumber] = React.useState<string | null>(null);
  const [bundlePrices, setBundlePrices] = React.useState<{ S: number; M: number; L: number } | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<ProductVariant | null>(null);
  const [finalPrice, setFinalPrice] = React.useState<number | null>(null);
  const [contactFirstName, setContactFirstName] = React.useState<string | null>(null);
  const [contactLastName, setContactLastName] = React.useState<string | null>(null);
  const [contactEmail, setContactEmail] = React.useState<string | null>(null);
  const [contactPhone, setContactPhone] = React.useState<string | null>(null);
  const [street, setStreet] = React.useState<string | null>(null);
  const [housenumber, setHousenumber] = React.useState<string | null>(null);
  const [city, setCity] = React.useState<string | null>(null);
  const [zipCode, setZipCode] = React.useState<string | null>(null);
  const [country, setCountry] = React.useState<string | null>(null);
  const [contactBirthdate, setContactBirthdate] = React.useState<Date | undefined>(undefined);
  const [customerGender, setCustomerGender] = React.useState<"male" | "female" | null>(null);
  const [preexistingCoverage, setPreexistingCoverage] = React.useState<boolean>(false);
  const [worldwideCoverage, setWorldwideCoverage] = React.useState<boolean>(false);
  const [yearlyDeductible, setYearlyDeductible] = React.useState<number>(300);
  const [coPayCoveragePercentage, setCoPayCoveragePercentage] = React.useState<number>(80);
  const [paymentInterval, setPaymentInterval] = React.useState<1 | 2 | 4 | 12>(12);
  const [paymentMethod, setPaymentMethod] = React.useState<"credit_card" | "ESR">("credit_card");
  const [startOfInsurance, setStartOfInsuranceState] = React.useState<Date>(getTodayAtMidnight());
  const [firstDue, setFirstDue] = React.useState<Date | null>(null);
  const [isOfferLoadingFromUrl, setIsOfferLoadingFromUrl] = React.useState<boolean>(false);
  const [isHydrated, setIsHydrated] = React.useState<boolean>(false);
  const [dispatchContract, setDispatchContract] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchInvoice, setDispatchInvoice] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchReminder, setDispatchReminder] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchEmail, setDispatchEmail] = React.useState<"customer" | "customer_and_agent" | "agent">(
    agentToken ? "agent" : "customer"
  );
  const [agentDispatchEmail, setAgentDispatchEmail] = React.useState<string | null>(null);
  const [offerLoadedFromUrl, setOfferLoadedFromUrl] = React.useState<boolean>(false);
  const [hasLeftConfirmationAfterOfferLoad, setHasLeftConfirmationAfterOfferLoad] = React.useState<boolean>(false);
  const [consultationProtocolDocumentId, setConsultationProtocolDocumentId] = React.useState<number | null>(null);
  const [consultationProtocolFilename, setConsultationProtocolFilename] = React.useState<string | null>(null);
  const [consultationProtocolPendingFile, setConsultationProtocolPendingFile] = React.useState<string | null>(null);
  const [consultationProtocolPendingFilename, setConsultationProtocolPendingFilename] = React.useState<string | null>(null);

  const hydrateFromStorage = React.useCallback(() => {
    const data = loadFunnelFromStorage(STORAGE_KEY);
    if (!data) {
      setDispatchEmail(agentToken ? "agent" : "customer");
      setIsHydrated(true);
      return;
    }
    const hydrated = hydrateValuesFromStorage(data, agentToken ? "agent" : "customer");

    setContractId(hydrated.contractId);
    setContractToken(hydrated.contractToken);
    setOfferAgentId(hydrated.offerAgentId ?? null);
    setStoredAgentId(hydrated.agentId ?? null);
    setPetType(hydrated.petType);
    setPetName(hydrated.petName);
    setBreed(hydrated.breed);
    setBreedLabel(hydrated.breedLabel);
    setBirthDate(hydrated.birthDate);
    setSelectedBreedType(hydrated.selectedBreedType);
    setGender(hydrated.gender);
    setIsCastrated(hydrated.isCastrated);
    setIsHouseCat(hydrated.isHouseCat);
    setSelectedDogSize(hydrated.selectedDogSize);
    setChipNumber(hydrated.chipNumber);
    setBundlePrices(hydrated.bundlePrices);
    setSelectedProduct(hydrated.selectedProduct);
    setFinalPrice(hydrated.finalPrice);
    setContactFirstName(hydrated.contactFirstName);
    setContactLastName(hydrated.contactLastName);
    setContactEmail(hydrated.contactEmail);
    setContactPhone(hydrated.contactPhone);
    setStreet(hydrated.street);
    setHousenumber(hydrated.housenumber);
    setCity(hydrated.city);
    setZipCode(hydrated.zipCode);
    setCountry(hydrated.country);
    setContactBirthdate(hydrated.contactBirthdate);
    setCustomerGender(hydrated.customerGender);
    setPreexistingCoverage(hydrated.preexistingCoverage);
    setWorldwideCoverage(hydrated.worldwideCoverage);
    setYearlyDeductible(hydrated.yearlyDeductible);
    setCoPayCoveragePercentage(hydrated.coPayCoveragePercentage);
    setPaymentInterval(hydrated.paymentInterval);
    setPaymentMethod(hydrated.paymentMethod);
    setStartOfInsuranceState(hydrated.startOfInsurance);
    setFirstDue(hydrated.firstDue);
    setIsOfferLoadingFromUrl(hydrated.isOfferLoadingFromUrl);
    setDispatchContract(hydrated.dispatchContract);
    setDispatchInvoice(hydrated.dispatchInvoice);
    setDispatchReminder(hydrated.dispatchReminder);
    setDispatchEmail(hydrated.dispatchEmail);
    setAgentDispatchEmail(hydrated.agentDispatchEmail);
    setWhitelabelPartner(hydrated.whitelabelPartner);
    setOfferLoadedFromUrl(hydrated.offerLoadedFromUrl);
    setHasLeftConfirmationAfterOfferLoad(hydrated.hasLeftConfirmationAfterOfferLoad);
    setConsultationProtocolDocumentId(hydrated.consultationProtocolDocumentId);
    setConsultationProtocolFilename(hydrated.consultationProtocolFilename);
    setConsultationProtocolPendingFile(hydrated.consultationProtocolPendingFile);
    setConsultationProtocolPendingFilename(hydrated.consultationProtocolPendingFilename);

    setIsHydrated(true);
  }, [
    agentToken,
    setContractId, setContractToken, setOfferAgentId, setStoredAgentId, setPetType, setPetName, setBreed, setBreedLabel, setBirthDate,
    setSelectedBreedType, setGender, setIsCastrated, setIsHouseCat, setSelectedDogSize, setBundlePrices,
    setSelectedProduct, setFinalPrice, setContactFirstName, setContactLastName, setContactEmail,
    setContactPhone, setStreet, setHousenumber, setCity, setZipCode, setCountry, setContactBirthdate,
    setCustomerGender, setPreexistingCoverage, setWorldwideCoverage, setYearlyDeductible,
    setCoPayCoveragePercentage, setPaymentInterval, setPaymentMethod, setStartOfInsuranceState, setFirstDue,
    setIsOfferLoadingFromUrl, setDispatchContract, setDispatchInvoice, setDispatchReminder, setDispatchEmail,
    setAgentDispatchEmail, setWhitelabelPartner, setOfferLoadedFromUrl, setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocumentId, setConsultationProtocolFilename,
    setConsultationProtocolPendingFile, setConsultationProtocolPendingFilename,
    setChipNumber
  ]);

  React.useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  React.useEffect(() => {
    if (!selectedProduct?.id) return;

    const syncSelectedProductLabels = () => {
      setSelectedProduct(createProductVariant(selectedProduct.id as "s" | "m" | "l"));
    };

    // Ensure hydrated products use the current locale immediately
    syncSelectedProductLabels();

    i18n.on("languageChanged", syncSelectedProductLabels);
    return () => {
      i18n.off("languageChanged", syncSelectedProductLabels);
    };
  }, [selectedProduct?.id]);

  React.useEffect(() => {
    const previousAgentId = previousContextAgentIdRef.current;
    if (contextAgentId) {
      setStoredAgentId(contextAgentId);
    } else if (previousAgentId) {
      setStoredAgentId(null);
    }
    previousContextAgentIdRef.current = contextAgentId;
  }, [contextAgentId]);

  React.useEffect(() => {
    if (!offerLoadedFromUrl || !petType || !breed || breedLabel || selectedBreedType === "mixed") return;

    let isActive = true;
    const hydrateBreedLabel = async () => {
      try {
        const breeds = await fetchBreeds(petType, i18n.language);
        const match = breeds.find((item) => item.code === breed);
        if (isActive && match?.translation) {
          setBreedLabel(match.translation);
        }
      } catch (error) {
        console.error("Failed to fetch breed translations for offer:", error);
      }
    };

    hydrateBreedLabel();
    return () => {
      isActive = false;
    };
  }, [offerLoadedFromUrl, petType, breed, breedLabel, selectedBreedType, i18n.language]);

  const handlePetSelection = (selectedPetType: "cat" | "dog") => {
    setPetType(selectedPetType);

    // Clear dependent data to prevent premature premium calculations
    setPetName(null);
    setBreed(null);
    setBreedLabel(null);
    setBirthDate(undefined);
    setSelectedBreedType(null);
    setGender(null);
    setIsCastrated(false);
    setIsHouseCat(false);
    setSelectedDogSize(null);
    setChipNumber(null);

    // Reset pricing-related state so queries stay disabled
    setBundlePrices(null);
    setSelectedProduct(null);
    setFinalPrice(null);
  };

  const handlePetDetailsSubmit = (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedPreexistingCoverage: boolean,
    submittedChipNumber: string | null
  ) => {
    setPetName(submittedPetName);
    setBreed(selectedBreed);
    setBreedLabel(submittedBreedLabel ?? null);
    setBirthDate(submittedBirthDate);
    setSelectedBreedType(submittedSelectedBreedType);
    setGender(submittedGender);
    setIsCastrated(submittedIsCastrated);
    setIsHouseCat(submittedIsHouseCat);
    setSelectedDogSize(submittedSelectedDogSize);
    setPreexistingCoverage(submittedPreexistingCoverage);
    setChipNumber(submittedChipNumber);
  };

  const handleProductVariantSelection = (product: ProductVariant) => {
    setSelectedProduct(product);
    setPreexistingCoverage(false);
    setWorldwideCoverage(false);
    setYearlyDeductible(300);
    setCoPayCoveragePercentage(80);
    setPaymentInterval(12);
    setPaymentMethod("credit_card");
    setStartOfInsurance(getTodayAtMidnight());
    setFinalPrice(null);
  };

  const handlePriceCustomizationSubmit = (
    product: ProductVariant,
    price: number,
    preexisting: boolean,
    worldwide: boolean,
    deductible: number,
    coPayCoverage: number,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => {
    setSelectedProduct(product);
    setFinalPrice(price);
    setPreexistingCoverage(preexisting);
    setWorldwideCoverage(worldwide);
    setYearlyDeductible(deductible);
    setCoPayCoveragePercentage(coPayCoverage);
    setPaymentInterval(interval);
    setPaymentMethod(method);
    setStartOfInsurance(startDate);
  };

  const handleContactDetailsSubmit = (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    city: string,
    zip: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => {
    setContactFirstName(firstName);
    setContactLastName(lastName);
    setContactEmail(email);
    setContactPhone(phone);
    setStreet(submittedStreet);
    setHousenumber(submittedHousenumber);
    setCity(city);
    setZipCode(zip);
    setCountry(country);
    setContactBirthdate(birthdate);
    setCustomerGender(gender === "" ? null : gender);
  };

  const setFunnelStateFromOffer = (offer: OfferResponse, agentIdOverride?: string | null): string => {
    const incomingContractId = offer.contractID || null;
    const incomingContractToken = offer.contractToken || null;
    if (incomingContractId !== contractId || incomingContractToken !== contractToken) {
      setConsultationProtocolDocumentId(null);
      setConsultationProtocolFilename(null);
      setConsultationProtocolPendingFile(null);
      setConsultationProtocolPendingFilename(null);
    }
    setContractId(incomingContractId);
    setContractToken(incomingContractToken);
    const resolvedOfferAgentId = agentIdOverride ?? offer.agentID ?? null;
    if (!isDirectAgent) {
      setOfferAgentId(resolvedOfferAgentId);
    } else {
      setOfferAgentId(null);
    }
    setOfferLoadedFromUrl(true);
    setHasLeftConfirmationAfterOfferLoad(false);
    const resolvedWhitelabelPartner = offer.whitelabelPartner ?? whitelabelPartner ?? null;
    setWhitelabelPartner(resolvedWhitelabelPartner);

    if (offer.product && offer.product.pets && offer.product.pets.length > 0) {
      const pet = offer.product.pets[0];
      setPetType(pet.species.toLowerCase() as "cat" | "dog");
      setPetName(pet.name || null);
      setBreed(pet.breed || null);
      setBreedLabel(null);
      setBirthDate(pet.birthdate ? new Date(pet.birthdate) : undefined);
      setGender(pet.gender || null);
      setIsCastrated(pet.castration || false);
      setIsHouseCat(pet.housecat || false);
      setChipNumber(pet.chipNumber || null);

      if (
        pet.breed === "mischlingskatze" ||
        pet.breed === "mischling_klein" ||
        (typeof pet.breed === "string" && pet.breed.includes("mischling_gross"))
      ) {
        setSelectedBreedType("mixed");
        if (pet.species === "Dog") {
          setSelectedDogSize(pet.breed === "mischling_klein" ? "small" : "large");
        }
      } else {
        setSelectedBreedType(pet.crossbreed ? "crossbreed" : "purebred");
      }

      if (pet.productBundle) {
        const bundleId = pet.productBundle.toLowerCase() as "s" | "m" | "l";
        setSelectedProduct(createProductVariant(bundleId));
      }

      setPreexistingCoverage(pet.coverages?.preexistingConditions?.requested || false);
      setWorldwideCoverage(pet.coverages?.worldwideCoverage?.requested || false);
      setYearlyDeductible(pet.coverages?.disease?.annualDeductible || 300);
      const claimDeductiblePercentage = pet.coverages?.disease?.claimDeductiblePercentageUpTo9Yrs;
      setCoPayCoveragePercentage(typeof claimDeductiblePercentage === "number" ? 100 - claimDeductiblePercentage : 80);
    }

    if (offer.customer && offer.customer.objectType === "NaturalPerson") {
      setContactFirstName(offer.customer.firstname || null);
      setContactLastName(offer.customer.name || null);
      setContactEmail(offer.customer.email || null);
      const phoneDetail = offer.customer.contactDetails?.find(cd => cd.category === "cellphone" || cd.category === "phone");
      setContactPhone(phoneDetail?.value || null);
      setStreet(offer.customer.address?.street || null);
      setHousenumber(offer.customer.address?.housenumber || null);
      setCity(offer.customer.address?.city || null);
      setZipCode(offer.customer.address?.zip || null);
      setCountry(offer.customer.address?.countryCode || null);

      setContactBirthdate(offer.customer.birthdate ? new Date(offer.customer.birthdate) : undefined);
      const offeredGender = typeof offer.customer.gender === "string" ? offer.customer.gender.toLowerCase() : null;
    setCustomerGender(offeredGender === "male" || offeredGender === "female" ? offeredGender : null);
    }

    // Set dispatch control settings
    setDispatchContract(offer.dispatchControl?.contract || "customer");
    setDispatchInvoice(offer.dispatchControl?.invoice || "customer");
    setDispatchReminder(offer.dispatchControl?.reminder || "customer");
    const offerEmailSetting =
      (offer.dispatchControl as any)?.offerEmails ||
      (offer.dispatchControl as any)?.offerEmail ||
      (offer.dispatchControl as any)?.orderEmails ||
      (offer.dispatchControl as any)?.orderEmail ||
      (agentToken ? "agent" : "customer");
    setDispatchEmail(offerEmailSetting);
    setAgentDispatchEmail(offer.dispatchControl?.agentEmail || null);

    setPaymentInterval(offer.yearlyPaymentFrequency?.frequency || 12);
    setPaymentMethod(offer.paymentMethod || "credit_card");
    setStartOfInsuranceState(offer.startOfInsurance ? new Date(offer.startOfInsurance) : getTodayAtMidnight());
    if (offer.firstDue) {
      const parsedFirstDue = new Date(offer.firstDue);
      setFirstDue(Number.isNaN(parsedFirstDue.getTime()) ? null : parsedFirstDue);
    } else {
      setFirstDue(null);
    }

    setFinalPrice(offer.grossPremiumProRata || null);

    const consultationDocument = offer.documents?.find((doc) => doc.kind === "consultation_protocol");
    if (consultationDocument && typeof consultationDocument.documentId === "number") {
      setConsultationProtocolDocumentId(consultationDocument.documentId);
      setConsultationProtocolFilename(consultationDocument.filename || null);
    } else {
      setConsultationProtocolDocumentId(null);
      setConsultationProtocolFilename(null);
    }
    if (consultationDocument?.file && consultationDocument.filename) {
      setConsultationProtocolPendingFile(consultationDocument.file);
      setConsultationProtocolPendingFilename(consultationDocument.filename);
    } else {
      setConsultationProtocolPendingFile(null);
      setConsultationProtocolPendingFilename(null);
    }
    return "routes.confirmation";
  };

  const handleResetFunnel = React.useCallback(() => {
    const defaults = getDefaultValues({ defaultDispatchEmail: agentToken ? "agent" : "customer" });

    setContractId(defaults.contractId);
    setContractToken(defaults.contractToken);
    setOfferAgentId(defaults.offerAgentId);
    setPetType(defaults.petType);
    setPetName(defaults.petName);
    setBreed(defaults.breed);
    setBreedLabel(defaults.breedLabel);
    setBirthDate(defaults.birthDate);
    setSelectedBreedType(defaults.selectedBreedType);
    setGender(defaults.gender);
    setIsCastrated(defaults.isCastrated);
    setIsHouseCat(defaults.isHouseCat);
    setSelectedDogSize(defaults.selectedDogSize);
    setChipNumber(defaults.chipNumber);
    setBundlePrices(defaults.bundlePrices);
    setSelectedProduct(defaults.selectedProduct);
    setFinalPrice(defaults.finalPrice);
    setContactFirstName(defaults.contactFirstName);
    setContactLastName(defaults.contactLastName);
    setContactEmail(defaults.contactEmail);
    setContactPhone(defaults.contactPhone);
    setStreet(defaults.street);
    setHousenumber(defaults.housenumber);
    setCity(defaults.city);
    setZipCode(defaults.zipCode);
    setCountry(defaults.country);
    setContactBirthdate(defaults.contactBirthdate);
    setCustomerGender(defaults.customerGender);
    setPreexistingCoverage(defaults.preexistingCoverage);
    setWorldwideCoverage(defaults.worldwideCoverage);
    setYearlyDeductible(defaults.yearlyDeductible);
    setCoPayCoveragePercentage(defaults.coPayCoveragePercentage);
    setPaymentInterval(defaults.paymentInterval);
    setPaymentMethod(defaults.paymentMethod);
    setStartOfInsuranceState(defaults.startOfInsurance);
    setFirstDue(defaults.firstDue);
    setIsOfferLoadingFromUrl(defaults.isOfferLoadingFromUrl);
    setDispatchContract(defaults.dispatchContract);
    setDispatchInvoice(defaults.dispatchInvoice);
    setDispatchReminder(defaults.dispatchReminder);
    setDispatchEmail(defaults.dispatchEmail);
    setAgentDispatchEmail(defaults.agentDispatchEmail);
    setStoredAgentId(defaults.agentId);
    setWhitelabelPartner(defaults.whitelabelPartner);
    setOfferLoadedFromUrl(defaults.offerLoadedFromUrl);
    setHasLeftConfirmationAfterOfferLoad(defaults.hasLeftConfirmationAfterOfferLoad);
    setConsultationProtocolDocumentId(defaults.consultationProtocolDocumentId);
    setConsultationProtocolFilename(defaults.consultationProtocolFilename);
    setConsultationProtocolPendingFile(defaults.consultationProtocolPendingFile);
    setConsultationProtocolPendingFilename(defaults.consultationProtocolPendingFilename);

    if (isLocalStorageAvailable()) {
      localStorage.removeItem(STORAGE_KEY);
    }

    setIsHydrated(true);
  }, [
    agentToken,
    setContractId, setContractToken, setOfferAgentId, setPetType, setPetName, setBreed, setBreedLabel, setBirthDate,
    setSelectedBreedType, setGender, setIsCastrated, setIsHouseCat, setSelectedDogSize, setBundlePrices,
    setSelectedProduct, setFinalPrice, setContactFirstName, setContactLastName, setContactEmail,
    setContactPhone, setStreet, setHousenumber, setCity, setZipCode, setCountry, setContactBirthdate,
    setCustomerGender, setPreexistingCoverage, setWorldwideCoverage, setYearlyDeductible,
    setCoPayCoveragePercentage, setPaymentInterval, setPaymentMethod, setStartOfInsuranceState,
    setIsOfferLoadingFromUrl, setDispatchContract, setDispatchInvoice, setDispatchReminder, setDispatchEmail,
    setAgentDispatchEmail, setStoredAgentId, setWhitelabelPartner, setOfferLoadedFromUrl, setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocumentId, setConsultationProtocolFilename,
    setConsultationProtocolPendingFile, setConsultationProtocolPendingFilename,
    setChipNumber
  ]);

  const setConsultationProtocolDocument = React.useCallback((documentId: number, filename: string) => {
    setConsultationProtocolDocumentId(documentId);
    setConsultationProtocolFilename(filename);
  }, []);

  const clearConsultationProtocolDocument = React.useCallback(() => {
    setConsultationProtocolDocumentId(null);
    setConsultationProtocolFilename(null);
  }, []);

  const setConsultationProtocolPending = React.useCallback((base64File: string, filename: string) => {
    setConsultationProtocolPendingFile(base64File);
    setConsultationProtocolPendingFilename(filename);
  }, []);

  const clearConsultationProtocolPending = React.useCallback(() => {
    setConsultationProtocolPendingFile(null);
    setConsultationProtocolPendingFilename(null);
  }, []);

  const funnelState: FunnelState = React.useMemo(() => {
    const effectiveAgentId = offerAgentId ?? contextAgentId ?? storedAgentId;
    return {
      contractId,
      contractToken,
      offerAgentId,
      agentId: effectiveAgentId,
      whitelabelPartner,
      petType,
      petName,
      breed,
      breedLabel,
      birthDate,
      selectedBreedType,
      gender,
      isCastrated,
      isHouseCat,
      selectedDogSize,
      chipNumber,
      bundlePrices,
      selectedProduct,
      finalPrice,
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhone,
      street,
      housenumber,
      city,
      zipCode,
      country,
      contactBirthdate,
      customerGender,
      preexistingCoverage,
      worldwideCoverage,
      yearlyDeductible,
      coPayCoveragePercentage,
      paymentInterval,
      paymentMethod,
      startOfInsurance,
      firstDue,
      isOfferLoadingFromUrl,
      isHydrated,
      dispatchContract,
      dispatchInvoice,
      dispatchReminder,
      dispatchEmail,
      agentDispatchEmail,
      offerLoadedFromUrl,
      hasLeftConfirmationAfterOfferLoad,
      consultationProtocolDocumentId,
      consultationProtocolFilename,
      consultationProtocolPendingFile,
      consultationProtocolPendingFilename,
    };
  }, [
    contractId,
    contractToken,
    contextAgentId,
    offerAgentId,
    storedAgentId,
    whitelabelPartner,
    petType,
    petName,
    breed,
    breedLabel,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    bundlePrices,
    selectedProduct,
    finalPrice,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    contactBirthdate,
    customerGender,
    preexistingCoverage,
    worldwideCoverage,
    yearlyDeductible,
    coPayCoveragePercentage,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    isOfferLoadingFromUrl,
    isHydrated,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId,
    consultationProtocolFilename,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
  ]);

  // Ensure customization values remain valid if age/pet type changes (e.g., user changes birthdate to 9+).
  React.useEffect(() => {
    if (!petType || !birthDate) return;

    const age = (() => {
      const today = new Date();
      let years = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) years--;
      return years;
    })();

    const speciesOptions = (productData as any).customizationOptions?.[petType];
    if (!speciesOptions) return;

    const ageBucket = age >= 9 ? speciesOptions.Over9Yrs : speciesOptions.upTo9Yrs;
    const deductibleOptions: number[] = ageBucket?.annualDeductibles || [];
    const recommendedDeductible = ageBucket?.annualDeductiblesRecommended ?? deductibleOptions[0] ?? 300;
    if (deductibleOptions.length && !deductibleOptions.includes(yearlyDeductible)) {
      setYearlyDeductible(recommendedDeductible);
    }

    const copayOptions: number[] = age >= 9
      ? speciesOptions.Over9Yrs?.claimDeductiblePercentageOver9Yrs || []
      : speciesOptions.upTo9Yrs?.claimDeductiblePercentageUpTo9Yrs || [];
    const recommendedCopay = age >= 9
      ? speciesOptions.Over9Yrs?.claimDeductiblePercentageOverTo9YrsRecommended ?? copayOptions[0] ?? coPayCoveragePercentage
      : speciesOptions.upTo9Yrs?.claimDeductiblePercentageUpTo9YrsRecommended ?? copayOptions[0] ?? coPayCoveragePercentage;
    if (copayOptions.length && !copayOptions.includes(coPayCoveragePercentage)) {
      setCoPayCoveragePercentage(recommendedCopay);
    }
  }, [petType, birthDate, yearlyDeductible, coPayCoveragePercentage]);

  const adjustFirstDueForStart = React.useCallback((currentFirstDue: Date | null, nextStart: Date) => {
    if (!currentFirstDue || Number.isNaN(currentFirstDue.getTime())) return currentFirstDue;
    if (Number.isNaN(nextStart.getTime())) return currentFirstDue;

    if (isAfter(nextStart, currentFirstDue)) {
      return addYears(currentFirstDue, 1);
    }

    const oneYearAfterStart = addYears(nextStart, 1);
    if (isAfter(currentFirstDue, oneYearAfterStart)) {
      let adjustedFirstDue = currentFirstDue;
      while (isAfter(adjustedFirstDue, oneYearAfterStart)) {
        adjustedFirstDue = subYears(adjustedFirstDue, 1);
      }
      return adjustedFirstDue;
    }

    return currentFirstDue;
  }, []);

  const setStartOfInsurance = React.useCallback((nextStart: Date) => {
    setStartOfInsuranceState(nextStart);
    setFirstDue((currentFirstDue) => adjustFirstDueForStart(currentFirstDue, nextStart));
  }, [adjustFirstDueForStart]);

  React.useEffect(() => {
    if (!isHydrated) return;
    const data: SerializedFunnelData = {
      contractId: funnelState.contractId,
      contractToken: funnelState.contractToken,
      offerAgentId: funnelState.offerAgentId,
      petType: funnelState.petType,
      petName: funnelState.petName,
      breed: funnelState.breed,
      breedLabel: funnelState.isHydrated ? funnelState.breedLabel : null, // Only serialize if hydrated
      birthDate: funnelState.birthDate ? funnelState.birthDate.toISOString() : null,
      selectedBreedType: funnelState.selectedBreedType,
      gender: funnelState.gender,
      isCastrated: funnelState.isCastrated,
      isHouseCat: funnelState.isHouseCat,
      selectedDogSize: funnelState.selectedDogSize,
      chipNumber: funnelState.chipNumber,
      bundlePrices: funnelState.bundlePrices,
      selectedProductId: funnelState.selectedProduct?.id ?? null,
      finalPrice: funnelState.finalPrice,
      contactFirstName: funnelState.contactFirstName,
      contactLastName: funnelState.contactLastName,
      contactEmail: funnelState.contactEmail,
      contactPhone: funnelState.contactPhone,
      street: funnelState.street,
      housenumber: funnelState.housenumber,
      city: funnelState.city,
      zipCode: funnelState.zipCode,
      country: funnelState.country,
      contactBirthdate: funnelState.contactBirthdate ? funnelState.contactBirthdate.toISOString() : null,
      customerGender: funnelState.customerGender,
      preexistingCoverage: funnelState.preexistingCoverage,
      worldwideCoverage: funnelState.worldwideCoverage,
      yearlyDeductible: funnelState.yearlyDeductible,
      coPayCoveragePercentage: funnelState.coPayCoveragePercentage,
      paymentInterval: funnelState.paymentInterval,
      paymentMethod: funnelState.paymentMethod,
      startOfInsurance: funnelState.startOfInsurance ? funnelState.startOfInsurance.toISOString() : null,
      firstDue:
        funnelState.firstDue && !Number.isNaN(funnelState.firstDue.getTime())
          ? funnelState.firstDue.toISOString()
          : null,
      isOfferLoadingFromUrl: funnelState.isOfferLoadingFromUrl,
      dispatchContract: funnelState.dispatchContract,
      dispatchInvoice: funnelState.dispatchInvoice,
      dispatchReminder: funnelState.dispatchReminder,
      dispatchEmail: funnelState.dispatchEmail,
      agentDispatchEmail: funnelState.agentDispatchEmail,
      agentId: funnelState.agentId,
      whitelabelPartner: funnelState.whitelabelPartner,
      offerLoadedFromUrl: funnelState.offerLoadedFromUrl,
      hasLeftConfirmationAfterOfferLoad: funnelState.hasLeftConfirmationAfterOfferLoad,
      consultationProtocolDocumentId: funnelState.consultationProtocolDocumentId,
      consultationProtocolFilename: funnelState.consultationProtocolFilename,
      consultationProtocolPendingFile: funnelState.consultationProtocolPendingFile,
      consultationProtocolPendingFilename: funnelState.consultationProtocolPendingFilename,
    };
    saveFunnelToStorage(STORAGE_KEY, data);
  }, [isHydrated, funnelState]);

  return {
    funnelState,
    setBundlePrices,
    setChipNumber,
    setPreexistingCoverage,
    setWorldwideCoverage,
    setYearlyDeductible,
    setCoPayCoveragePercentage,
    setPaymentInterval,
    setPaymentMethod,
    setStartOfInsurance,
    setFirstDue,
    setSelectedProduct,
    setFinalPrice,
    setIsOfferLoadingFromUrl,
    setDispatchContract,
    setDispatchInvoice,
    setDispatchReminder,
    setDispatchEmail,
    setAgentDispatchEmail,
    setWhitelabelPartner,
    setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocument,
    clearConsultationProtocolDocument,
    setConsultationProtocolPending,
    clearConsultationProtocolPending,
    handlePetSelection,
    handlePetDetailsSubmit,
    handleProductVariantSelection,
    handlePriceCustomizationSubmit,
    handleContactDetailsSubmit,
    setFunnelStateFromOffer,
    handleResetFunnel,
  };
};
